'use strict';

import * as THREE from "three";
import {
    BackSide,
    DoubleSide,
    FrontSide,
    LinearFilter,
    LinearMipmapLinearFilter,
    ShaderMaterial,
    NearestFilter,
    Color,
    Matrix3,
}from 'three'

import {isMobile, isTablet, projectMode, settingsData, threeJsVariables} from '../../settings';
import { DebugTilesRenderer, TilesRenderer } from '3d-tiles-renderer';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import {get_url_param} from '../../functions';
import { Group } from 'three';
import { ViewMode} from "../../common";
import { EnvironmentTypes } from '../../common';
import { GUI } from 'three/examples/jsm/libs/dat.gui.module';
import { isDesktopMode } from '../language/langSwitcher';
import { Vector2 } from 'three';
import gsap from 'gsap';

const envGroup = new Group();

/*export async function loadMultiEnvironment () {
    const isEnv = threeJsVariables.scene.getObjectByName('async env');
    if (isEnv) {
        environmentShow();
        return;
    }

    const envPromises = [];
    const { cesium } = config.cesiumEnvironment;

    cesium.cesiumContent.forEach(env =>{
        const partials = loadEnv(env);
        envPromises.push(partials);
    });

    const allTiles = Promise.all(envPromises);
    allTiles.then((response) => setupAndAddToSceneAllEnvironmentParts(response));
}*/

export const basic_tile = new ShaderMaterial({
    uniforms: {
        map         : { value: null },
        hmap        : { value: null },
        uvTransform : { value: new Matrix3() },
        fogColor   : { value: new Color()  },
        fogDensity : { value: 0.0  },
        //color      : { value: new Color() },
        //gamma      : { value: 1.0 },
    },
    vertexShader: [
        'uniform sampler2D hmap;',
        'uniform mat3 uvTransform;',
        'varying vec2 lUv;',
        'varying float z;',
        'void main() {',
            'lUv = (uvTransform*vec3( uv, 1 )).xy;',
            'vec4 rp = modelMatrix * vec4( position, 1.0 );',
            'float size = 512.0;',
            'float scale = 1.0/1024.0;',
            'float h = texture2D(hmap, vec2((rp.x+size)*scale,(rp.z+size)*scale)).r;',
            'if (h!=0.0){',
                'rp.y = (h-0.5)*128.0;',
            '}',
            'gl_Position = projectionMatrix * viewMatrix * rp;',
            'z = -gl_Position.z;',
        '}'].join(''),
    fragmentShader: [
        'uniform vec3 fogColor;',
        'uniform float fogDensity;',
        'uniform sampler2D map;',
        'varying vec2 lUv;',
        'varying float z;',
        //'uniform float gamma;',
        //'uniform vec3 color;',
        'void main(){',
            'vec4 c = texture2D(map, lUv);',
            //'c = vec4(c.rgb * color * gamma,1.0);',
            // 'gl_FragColor = c;',
            'float fogDepth = z;',
            'float fogFactor = 1.0 - exp( -fogDensity * fogDensity * fogDepth * fogDepth );',
            'gl_FragColor = vec4(mix( c.rgb, fogColor, fogFactor ), 1.0);',
            '}'].join(''),
    side: DoubleSide,
    depthTest: true,
    depthWrite: true,
    transparent: false,
});

function correctTilesPosition (tiles,env) {
    const {position, rotation, scale} = env;

    tiles.group.position.set(position.x,position.y,position.z);
    tiles.group.rotation.set(rotation.x,rotation.y,rotation.z);
    tiles.group.scale.set(scale.x,scale.y,scale.z);

}

export async function environmentToggle () {

    if (projectMode.LiteMode.isActive()) return;

    const {active,environmentTypes} = config.resources.environments;
    if (!active){
        return;
    }

    let env;

    if (isDesktopMode()){
        env = environmentTypes.filter(type => type.apply.filter(envType => envType.device === 'desktop')[0])[0];
    } else if (isMobile){
        env = environmentTypes.filter(type => type.apply.filter(envType => envType.device === 'mobile')[0])[0];
    } else {
        env = environmentTypes.filter(type => type.apply.filter(envType => envType.device === 'web')[0])[0];
    }

    switch (env.type) {
        case EnvironmentTypes.CesiumEnv:
                loadCesiumEnvironment(env);
            break;
        // case EnvironmentTypes.CesiumMultiEnv:
                // loadMultiEnvironment();
            // break;
        case EnvironmentTypes.TilesEnv:
               await loadTilesEnvironment(env);
            break;
    }
}

function setAdditionalSettings (tile) {

    if (isMobile){
        const mobileSettings = tile.userData.settings.mobile;
        if (mobileSettings){
            Object.entries(mobileSettings).forEach(([key,value]) =>{
               tile[key] = value;
            });
        }
    } else {
        const desktopSettings = tile.userData.settings.desktop;
        if (desktopSettings){
            Object.entries(desktopSettings).forEach(([key,value]) =>{
                tile[key] = value;
            });
        }
    }
}

function setupAndAddToSceneAllEnvironmentParts (response) {
    const envGroup = new Group();
    envGroup.name = 'async env';
    response.forEach(env => {
        setAdditionalSettings(env);
        envGroup.add(env.group);
    });

    threeJsVariables.scene.add(envGroup);

    console.log(envGroup);
    console.log(response);

    function update(){
        requestAnimationFrame(update);
        if (settingsData.activeViewMode === ViewMode.Orbit){
            response.forEach(env =>{
               env.update();
            });
        }
    }

    update();
}

function loadEnv (env) {
    return new Promise(resolve => {
       const { cesium } = config.cesiumEnvironment;
       let url = new URL( `https://api.cesium.com/v1/assets/${env.id}/endpoint`);
       url.searchParams.append('access_token', cesium.cesiumAccessToken);

       let tiles;

        fetch(url, {mode: 'cors'})
          .then((res) => {

              if (res.ok) {
                  return res.json();
              } else {
                  return Promise.reject(`${res.status} : ${res.statusText}`);
              }

          })
          .then((json) => {
              url = new URL(json.url);
              const version = url.searchParams.get('v');

              tiles = new TilesRenderer(url);
              tiles.userData = {
                  settings: env,
                  isActive: true,
                  tilesOnScene: false,
              };
              tiles.setCamera(threeJsVariables.perspectiveCamera);
              tiles.setResolutionFromRenderer(threeJsVariables.perspectiveCamera, threeJsVariables.renderer);
              tiles.fetchOptions.headers = {};
              tiles.fetchOptions.headers.Authorization = `Bearer ${json.accessToken}`;

              tiles.preprocessURL = (uri) => {
                  const exp = parseJwt(json.accessToken).exp;
                  const now = new Date().getTime() / 1000;

                  if (exp < now){
                      const tokenRequest = getNewToken();
                      tokenRequest.then((newToken) =>{
                          json.accessToken = newToken;
                          uri = new URL( uri );
                          if ( /^http/.test( uri.protocol ) ) {

                              uri.searchParams.append( 'v', version );

                          }
                          return uri.toString();
                      });
                  } else {
                      uri = new URL( uri );
                      if ( /^http/.test( uri.protocol ) ) {

                          uri.searchParams.append( 'v', version );

                      }
                      return uri.toString();
                  }
              };

              tiles.onLoadTileSet = (e) => {
                  const box = new THREE.Box3();
                  const matrix = new THREE.Matrix4();
                  tiles.getOrientedBounds(box, matrix);
                  const position = new THREE.Vector3().setFromMatrixPosition(matrix);
                  const distanceToEllipsoidCenter = position.length();

                  const surfaceDirection = position.normalize();
                  const up = new THREE.Vector3(0, 1, 0);
                  const rotationToNorthPole = rotationBetweenDirections(surfaceDirection, up);

                  // tiles.group.quaternion.x = rotationToNorthPole.x;
                  // tiles.group.quaternion.y = rotationToNorthPole.y;
                  // tiles.group.quaternion.z = rotationToNorthPole.z;
                  // tiles.group.quaternion.w = rotationToNorthPole.w;
                  // tiles.group.position.y = -distanceToEllipsoidCenter;
                  correctTilesPosition(tiles,env);
              };

              tiles.onLoadModel = function (scene) {
                  scene.traverse(objMesh => {
                      if (objMesh.material && objMesh.material.map ) {
                          const image = new THREE.CanvasTexture(objMesh.material.map.image);
                          image.wrapS = THREE.RepeatWrapping;
                          image.wrapT = THREE.RepeatWrapping;
                          image.offset.set(0,0);
                          image.repeat.set(1,1);
                          image.flipY = false;
                          objMesh.material = new THREE.MeshBasicMaterial({
                              map: image,
                          });
                          // objMesh.material.map.encoding = THREE.LinearEncoding;
                      }
                  });
              };

              setup();
          })
          .catch(err => {
              console.error('Unable to get ion tileset:', err);
          });


        function setup() {
            tiles.fetchOptions.mode = 'cors';

            const dracoLoader = new DRACOLoader();
            dracoLoader.setDecoderPath('js/libs/gltf/');

            const loader = new GLTFLoader(tiles.manager);
            loader.setDRACOLoader(dracoLoader);

            tiles.manager.addHandler(/\.gltf$/, loader);

            resolve(tiles);
        }
    });
}

export function loadCesiumEnvironment(env) {
    const {cesiumSettings,scale, position,rotation} = env.settings;

    const isEnv = threeJsVariables.scene.getObjectByName('async env');
    if (isEnv) {
        environmentShow();
        return;
    }

    let url;
    let tiles;
    const offsetParent = new THREE.Group();

    const cesiumIdManual = get_url_param('cesiumid');
    const tokenManual = get_url_param('token');
    let urlManual = `https://api.cesium.com/v1/assets/${cesiumIdManual}/endpoint`;
    const cesiumUrl = cesiumSettings.cesiumURL.replace('{{cesiumAssetsID}}', String(cesiumSettings.cesiumAssetsID));

    url = new URL( cesiumIdManual && tokenManual ? urlManual : cesiumUrl);
    url.searchParams.append('access_token', tokenManual || cesiumSettings.cesiumAccessToken);

    fetch(url, {mode: 'cors'})
      .then((res) => {

          if (res.ok) {
              return res.json();
          } else {
              return Promise.reject(`${res.status} : ${res.statusText}`);
          }

      })
      .then((json) => {
          url = new URL(json.url);
          const version = url.searchParams.get('v');

          settingsData.needsRerender = true;

          tiles = new TilesRenderer(url);

          // window.tiles = tiles;
          tiles.displayBoxBounds = true;
          tiles.userData = {
              isActive: true,
              tilesOnScene: false,
          };
          threeJsVariables.tilesRenderer = tiles;
          tiles.setCamera(threeJsVariables.perspectiveCamera);
          tiles.setResolutionFromRenderer(threeJsVariables.perspectiveCamera, threeJsVariables.renderer);
          tiles.fetchOptions.headers = {};
          tiles.fetchOptions.headers.Authorization = `Bearer ${json.accessToken}`;

          setEnvironmentSettings(env,tiles);

          // if (isMobile){
          //     tiles.errorTarget = 18;
          //     tiles.errorThreshold = 1000;
          // } else {
          //     tiles.errorTarget = 5;
          //     tiles.errorThreshold = 1000;
          // }

          tiles.manager.onProgress = function (url, itemsLoaded, itemsTotal) {
             // if (!tiles.userData.isActive){
             //     tiles.lruCache.itemList = [];
             //     tiles.lruCache.callbacks.clear();
             //     tiles.downloadQueue.items=[];
             //     tiles.downloadQueue.callbacks.clear();
             // }
          };

          tiles.manager.onLoad = () =>{
              // if (tiles.downloadQueue.callbacks.size === 0){
              //     tiles.userData.tilesOnScene = true;
              // }
          };

          tiles.preprocessURL = (uri) => {
              const exp = parseJwt(json.accessToken).exp;
              const now = new Date().getTime() / 1000;

              if (exp < now){
                  const tokenRequest = getNewToken(env);
                  tokenRequest.then((newToken) =>{
                      json.accessToken = newToken;
                      uri = new URL( uri );
                      if ( /^http/.test( uri.protocol ) ) {

                          uri.searchParams.append( 'v', version );

                      }
                      return uri.toString();
                  });
              } else {
                  uri = new URL( uri );
                  if ( /^http/.test( uri.protocol ) ) {

                      uri.searchParams.append( 'v', version );

                  }
                  return uri.toString();
              }
          };

          tiles.onLoadTileSet = (e) => {
              const box = new THREE.Box3();
              const matrix = new THREE.Matrix4();
              tiles.getOrientedBounds(box, matrix);
              const position = new THREE.Vector3().setFromMatrixPosition(matrix);
              const distanceToEllipsoidCenter = position.length();

              const surfaceDirection = position.normalize();
              const up = new THREE.Vector3(0, 1, 0);
              const rotationToNorthPole = rotationBetweenDirections(surfaceDirection, up);

              tiles.group.quaternion.x = rotationToNorthPole.x;
              tiles.group.quaternion.y = rotationToNorthPole.y;
              tiles.group.quaternion.z = rotationToNorthPole.z;
              tiles.group.quaternion.w = rotationToNorthPole.w;
              tiles.group.position.y = -distanceToEllipsoidCenter;
          };

          tiles.onLoadModel = function (scene) {
              // tiles.userData.isActive && tiles.update();
              // console.log('update tiles render');
              scene.traverse(objMesh => {
                  if (objMesh.material && objMesh.material.map ) {
                      // const image = new THREE.CanvasTexture(objMesh.material.map.image);
                      // image.wrapS = THREE.RepeatWrapping;
                      // image.wrapT = THREE.RepeatWrapping;
                      // image.offset.set(0,0);
                      // image.repeat.set(1,1);
                      // image.flipY = false;
                      objMesh.material = new THREE.MeshBasicMaterial({
                          map: objMesh.material.map,
                          side: THREE.DoubleSide,
                      });
                      objMesh.material.map.encoding = THREE.LinearEncoding;
                  }
              });
          };

          setupTiles();
      })
      .catch(err => {
          console.error('Unable to get ion tileset:', err);
      });


    function setupTiles() {
        tiles.fetchOptions.mode = 'cors';

        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath('js/libs/gltf/');

        const loader = new GLTFLoader(tiles.manager);
        loader.setDRACOLoader(dracoLoader);

        tiles.manager.addHandler(/\.gltf$/, loader);
        offsetParent.add(tiles.group);
    }

    if (!cesiumIdManual && !tokenManual){
        offsetParent.scale.set(scale.x, scale.y, scale.z);
        offsetParent.position.set(position.x, position.y, position.z);
        offsetParent.rotation.set(rotation.x, rotation.y, rotation.z);
    }

    offsetParent.name = 'async env';
    offsetParent.updateMatrixWorld(true);

    threeJsVariables.scene.add(offsetParent);

    // renderLoop();

    // function renderLoop() {
    //     requestAnimationFrame(renderLoop);
    //     if (!tiles) return;
       
         // tiles.userData.isActive && tiles.update();
        // tiles.update();

        

        // if (tiles.group.children.length === 0){
        //     // console.log('render');
        //     tiles.userData.isActive && tiles.update();
        // }
        //
        // if (settingsData.drag_move || settingsData.wheelIsUse){
        //    tiles.userData.isActive && tiles.update();
        // }
    // }

}

function setEnvironmentSettings (env,tilesRender) {
    let settings;

    if (isDesktopMode()){
        settings = env.apply.filter(envType => envType.device === 'desktop')[0];
    } else if (isMobile){
        settings = env.apply.filter(envType => envType.device === 'mobile')[0];
    }else{
        settings = env.apply.filter(envType => envType.device === 'web')[0];
    }

    const config = settings.config;

    if (devModeEnvironmentParams.isGuiOnScene){
        tilesRender.errorTarget = devModeEnvironmentParams.errorTarget;
        tilesRender.errorThreshold = devModeEnvironmentParams.errorThreshold;
        tilesRender.maxDepth = devModeEnvironmentParams.maxDepth;
    } else {
        Object.entries(config).forEach(([key,value]) =>{
            tilesRender[key] = value;
        });
    }
}

function getNewToken (env) {
    const {cesiumAccessToken,cesiumAssetsID,cesiumURL} = env.settings.cesiumSettings;

    const cesiumUrl = cesiumURL.replace('{{cesiumAssetsID}}', String(cesiumAssetsID));

    let url = new URL( cesiumUrl);
    url.searchParams.append('access_token', cesiumAccessToken);

    return new Promise(async (resolve, reject) => {
       const response = await fetch(url, {mode: 'cors'});
       if (response.ok){
           resolve(response.json().accessToken);
       } else {
           reject(`${response.status} : ${response.statusText}`);
       }
    });
}

function parseJwt (token) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
};


function rotationBetweenDirections(dir1, dir2) {

    const rotation = new THREE.Quaternion();
    const a = new THREE.Vector3().crossVectors(dir1, dir2);
    rotation.x = a.x;
    rotation.y = a.y;
    rotation.z = a.z;
    rotation.w = 1 + dir1.clone().dot(dir2);
    rotation.normalize();

    return rotation;
}

const devModeEnvironmentParams = {
    displayBoxBounds: false,
    errorTarget: 5,
    errorThreshold: 12,
    maxDepth: 20,
    isGuiOnScene: false,
    'changeDisplayBoxBounds': () => changeDisplayBoxBounds()
}

function changeDisplayBoxBounds() {
    const {scene} = threeJsVariables;
    devModeEnvironmentParams.displayBoxBounds = !devModeEnvironmentParams.displayBoxBounds;

    const asyncEnv = threeJsVariables.scene.getObjectByName('async env');
    asyncEnv && scene.remove(asyncEnv);

    environmentToggle();
}
function changeErrorTarget(value) {

}
function changeerrorThreshold(value) {

}

function addGuiHelper() {
    if (devModeEnvironmentParams.isGuiOnScene) return;

    const {gui} = threeJsVariables;

    const environmentFolder = gui.addFolder('environment');

    environmentFolder.add( devModeEnvironmentParams, 'errorTarget', 0, 500 ).step( 0.01 ).onChange( function ( value ) {
        threeJsVariables.tilesRenderer.errorTarget = value;
    });

    environmentFolder.add( devModeEnvironmentParams, 'errorThreshold', 0, 1000 ).step( 0.01 ).onChange( function ( value ) {
        threeJsVariables.tilesRenderer.errorThreshold = value;
    });

    environmentFolder.add( devModeEnvironmentParams, 'maxDepth', 0, 50 ).step( 1 ).onChange( function ( value ) {
        threeJsVariables.tilesRenderer.maxDepth = value;
    });

    environmentFolder.add(devModeEnvironmentParams, 'changeDisplayBoxBounds');

    devModeEnvironmentParams.isGuiOnScene = true;
}

export const setHMap = (t)=>{
    t.minFilter = NearestFilter
    t.magFilter = NearestFilter
    t.generateMipmaps = false
    t.flipY = false

    basic_tile.uniforms.hmap.value = t

    let tilesRender =  threeJsVariables.tilesRenderer
    if (tilesRender){
        tilesRender.forEachLoadedModel((scene,tile)=>{
            scene.traverse(o =>{
                if ( o.material) {
                    o.material.uniforms.hmap.value = t
                }
            })
        })
    }else{
        let a = threeJsVariables.scene.getObjectByName('async env')
        a.traverse(o=>{
            if ( o.material) {
                o.material.uniforms.hmap.value = t
            }
        })
    }
}

export function addTilesToScene() {
    if (!isMobile) threeJsVariables.scene.add(envGroup);

    if (projectMode.LiteMode.isActive()){

        // projectMode.LiteMode.addToScene();

       return new Promise(resolve => {
           setTimeout(() => {
               resolve();
           },200);
           // const liteModeInterval = setInterval(() => {
           //     const checkLayer = threeJsVariables.scene.getObjectByName('Lite mode');
           //     if (checkLayer){
           //         clearInterval(liteModeInterval);

               // }
           // },100);
       });
    }

    return new Promise(resolve => {
        const int = setInterval(() =>{
            if (threeJsVariables.tilesRenderer && threeJsVariables.tilesRenderer.visibleTiles.size) {
                clearInterval(int);
                resolve();
            } else if (!threeJsVariables.tilesRenderer && threeJsVariables.scene.getObjectByName('async env')) {
                clearInterval(int);
                resolve();
            }
        },200)
    });
}

export async function loadTilesEnvironment(env) {
    const isEnv = threeJsVariables.scene.getObjectByName('async env');
    if (isEnv) {
        // environmentShow();
        await environmentShowV2();
        return;
    }

    const {position,rotation,scale, tilesSettings} = env.settings;
    const {cdnRepository,projectFolder} = config;
    const tilesUrl = `${cdnRepository}/${projectFolder}/resources/tiles/${tilesSettings.path}`;

    settingsData.needsRerender = true;
    let tilesRenderer;

    if (get_url_param('dev')){
        tilesRenderer = new DebugTilesRenderer(tilesUrl);
        tilesRenderer.displayBoxBounds = devModeEnvironmentParams.displayBoxBounds;
    } else {
        tilesRenderer = new TilesRenderer(tilesUrl);
    }

    threeJsVariables.tilesRenderer = tilesRenderer;
    settingsData.totalTilesCount = 0;

    const surfaceGroup = new Group();
    surfaceGroup.name = 'surfaceGroup';

    const envGroup = new Group();
    envGroup.name = 'async env';
    envGroup.add(surfaceGroup);

    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('js/libs/gltf/');

    const loader = new GLTFLoader(tilesRenderer.manager);
    loader.setDRACOLoader(dracoLoader);

    tilesRenderer.manager.addHandler(/\.gltf$/, {

        parse(...args) {
            return loader.parse(...args);
        }
    });

    tilesRenderer.setCamera(threeJsVariables.perspectiveCamera);
    tilesRenderer.setResolutionFromRenderer(threeJsVariables.perspectiveCamera, threeJsVariables.renderer);
    // tilesRenderer.setResolution(threeJsVariables.perspectiveCamera,new Vector2(window.innerWidth / 1.5, window.innerHeight / 1.5));

    setEnvironmentSettings(env,tilesRenderer);

    // tilesRenderer.displayBoxBounds = true;

    if (get_url_param('dev')){

        addGuiHelper();
    }

    // tilesRenderer.manager.onProgress = (tiles)=>{
    //     // console.log(tiles);
    // };

    // tilesRenderer.preprocessURL = (uri) => {
    //     // const domain = 'https://cloud.skylineglobe.com/sg/b3dm/NeveTzedek3.420160/1416/';
    //     // uri = new URL(domain + uri);
    //     // return uri;
    // };

    //tilesRenderer.manager.onLoad = (tiles) =>{
        // if (tilesRenderer.frameCount === tilesRenderer.root.children.length){
        //     removePreloader();
        //     flyCameraAnimation();
        // }
    //};

    tilesRenderer.onLoadTileSet = (tiles)=> {
        const tilesType = Object.keys(tiles.root.boundingVolume)[0];

        switch (tilesType) {
            case 'box':

                break;
            case 'sphere':
                const sphere = new THREE.Sphere();
                tilesRenderer.getBoundingSphere(sphere);
                const dir = new THREE.Vector3().subVectors(new THREE.Vector3(),sphere.center);
                tilesRenderer.group.position.set(dir.x,dir.y,dir.z);

                const position = new THREE.Vector3().copy(tilesRenderer.group.position);

                const surfaceDirection = position.normalize();
                const up = new THREE.Vector3(0, -1, 0);
                const rotationToNorthPole = rotationBetweenDirections(surfaceDirection, up);

                surfaceGroup.quaternion.x = rotationToNorthPole.x;
                surfaceGroup.quaternion.y = rotationToNorthPole.y;
                surfaceGroup.quaternion.z = rotationToNorthPole.z;
                surfaceGroup.quaternion.w = rotationToNorthPole.w;

                break;
        }

        settingsData.needsRerender = true;
    };

    tilesRenderer.onLoadModel = function ( tiles ) {
        settingsData.needsRerender = true;
        tiles.traverse(objMesh =>{
            if ( objMesh.material) {

                let map = objMesh.material.map
                objMesh.material = basic_tile.clone()
                objMesh.material.uniforms.map.value = map
                objMesh.material.uniforms.hmap.value = basic_tile.uniforms.hmap.value

                objMesh.castShadow = false;
                map.encoding = THREE.LinearEncoding;
                map.wrapS = THREE.RepeatWrapping;
                map.wrapT = THREE.RepeatWrapping;
                map.offset.set(0,0);
                map.repeat.set(1,1);

                map.anisotropy = 2
                map.magFilter = LinearFilter
                map.minFilter = LinearMipmapLinearFilter

                settingsData.totalTilesCount++;
            }
        });
    };

    surfaceGroup.add(tilesRenderer.group);

    envGroup.scale.set(scale.x,scale.y,scale.z);
    envGroup.position.set(position.x,position.y,position.z);
    envGroup.rotation.set(rotation.x,rotation.y,rotation.z);

    threeJsVariables.scene.add( envGroup );
    threeJsVariables.tilesRenderer = tilesRenderer;

    // renderLoop();

    // function renderLoop() {
    //     requestAnimationFrame( renderLoop );
        // if ( needsRerender ) {
        //     needsRerender = false;
        // if (needsRerender && settingsData.activeViewMode === ViewMode.Orbit)
        //     tilesRenderer.update();
        // }
    // }
}

export function renderLoop() {
    if (threeJsVariables.tilesRenderer){
        threeJsVariables.tilesRenderer.update();
    }
    settingsData.needsRerender = false;
}

export function environmentHide () {
    const asyncEnv = threeJsVariables.scene.getObjectByName('async env');
    if (asyncEnv) {
        if (!asyncEnv.visible){
            return;
        }

        asyncEnv.traverse(element => {
            if (element.isMesh) {
              gsap.to(element.material, {
                    duration: .2,
                    opacity: 0,
                    onStart:()=>{
                        element.material.transparent = true;
                    },
                    onComplete: () => {
                        asyncEnv.visible = false;
                    }
              });
            }
        });
    }
}

export function environmentShow () {
    const isEnv = threeJsVariables.scene.getObjectByName('async env');
    if (isEnv) {
        isEnv.visible = true;

        threeJsVariables.tilesRenderer.forEachLoadedModel((scene,tile)=>{
            scene.traverse(child =>{
                if (child.isMesh){
                    const envPromise = new Promise(resolve => {
                        gsap.to(child.material,{
                            duration: 1,
                            opacity: 1,
                            onStart:()=>{
                                scene.visible = true;
                            },
                            onComplete:()=>{
                                child.material.transparent = false;
                                resolve(true);
                            }
                        });
                    });

                    // settingsData.animationsList.push(envPromise);
                }
            });
        });

    }
}

export function environmentShowV2 () {
    const animations = [];

    const isEnv = threeJsVariables.scene.getObjectByName('async env');
    if (isEnv) {
        isEnv.visible = true;

        if (threeJsVariables.tilesRenderer){
            threeJsVariables.tilesRenderer.forEachLoadedModel((scene,tile)=>{
                scene.traverse(child =>{
                    if (child.isMesh){
                        const envPromise = new Promise(resolve => {
                            gsap.to(child.material,{
                                duration: 1,
                                opacity: 1,
                                onStart:()=>{
                                    scene.visible = true;
                                },
                                onComplete:()=>{
                                    child.material.transparent = false;
                                    resolve(true);
                                }
                            });
                        });
    
                        animations.push(envPromise);
                    }
                });
            });
        }else{
            isEnv.traverse(child =>{
                if (child.isMesh){
                    const envPromise = new Promise(resolve => {
                        gsap.to(child.material,{
                            duration: 1,
                            opacity: 1,
                            onStart:()=>{
                                child.visible = true
                            },
                            onComplete:()=>{
                                child.material.transparent = false
                                resolve(true)
                            }
                        })
                    })

                    animations.push(envPromise)
                }
            });
        }

    }

    return Promise.all(animations);
}
